import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/feature/AppLayout";
import { useAuthContext } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  fetchTicketEmailConfig,
  pollTicketEmailInbox,
  testTicketEmailConnectivity,
  updateTicketEmailAutomations,
  updateTicketEmailConnection,
  updateTicketEmailTemplates,
  webhookUrlForProvider,
} from "@/api/ticketEmailApi";
import { getWidgetSettings, postWidgetBootstrap } from "@/api/widgetGeneratorApi";
import {
  isSesIamRoleMode,
  SES_AUTH_ACCESS_KEYS,
  SES_AUTH_IAM_ROLE,
  sesShowsCredentialFields,
} from "@/pages/email-service/lib/sesAuthMode";
import {
  buildEmailPreviewVariables,
  DEFAULT_EMAIL_PRIMARY_COLOR,
  substituteEmailTemplateVars,
  themedDefaultTemplatesByKey,
} from "@/lib/emailTemplateTheme";

const PROVIDER_OPTIONS = [
  { value: "smtp", label: "SMTP / IMAP" },
  { value: "resend", label: "Resend" },
  { value: "ses", label: "AWS SES" },
  { value: "brevo", label: "Brevo" },
  { value: "mailgun", label: "Mailgun" },
  { value: "mandrill", label: "Mandrill (Mailchimp Transactional)" },
  { value: "zendesk", label: "Zendesk (ticket sync)" },
];

/** Per-provider UI: which fields to show and how to label them (matches backend adapters). */
const PROVIDER_SCHEMA = {
  smtp: {
    label: "SMTP / IMAP",
    summary: "Send mail over SMTP. Inbound uses IMAP polling (not HTTP webhooks).",
    inboundNote:
      "Inbound mail is polled automatically every minute when enabled on the server. Use Refresh to pull new mail now, or save settings first if you changed credentials.",
    defaultProviderConfig: {},
    fields: {
      clientSecret: {
        show: true,
        label: "SMTP password",
        placeholder: "••••••••",
        hint: "Leave blank to keep the password already stored on the server.",
      },
      clientToken: {
        show: true,
        label: "SMTP username",
        placeholder: "notifications@yourdomain.com",
        hint: "Often the mailbox username or full email address.",
      },
      accessApiUrl: {
        show: true,
        label: "SMTP host",
        placeholder: "smtp.example.com",
        hint: "Hostname only, or smtp://host:587",
      },
      tenantOrDomain: {
        show: true,
        label: "IMAP host",
        placeholder: "imap.example.com",
        hint: "Where inbound mail is read (can match SMTP host).",
      },
      apiScope: { show: false },
      providerConfigJson: { show: false },
      webhookSigningSecret: { show: false },
      webhookUrl: { show: false },
    },
  },
  resend: {
    label: "Resend",
    summary: "HTTPS API (api.resend.com). Inbound via signed webhooks.",
    inboundNote: "Add the webhook URL below in the Resend dashboard for this domain.",
    defaultProviderConfig: {},
    fields: {
      clientSecret: {
        show: true,
        label: "API key",
        placeholder: "re_…",
        hint: "Resend API key (sent as Bearer token). Leave blank to keep existing.",
      },
      clientToken: { show: false },
      accessApiUrl: {
        show: true,
        label: "API base URL (optional)",
        placeholder: "https://api.resend.com",
        hint: "Override only if you use a proxy; default is Resend production API.",
      },
      tenantOrDomain: { show: false },
      apiScope: { show: false },
      providerConfigJson: { show: false },
      webhookSigningSecret: {
        show: true,
        label: "Webhook signing secret (optional)",
        placeholder: "whsec_…",
        hint: "Svix signing secret from Resend if you enable signed webhooks.",
      },
      webhookUrl: { show: true },
    },
  },
  ses: {
    label: "AWS SES",
    summary: "Amazon SES v2 send API via access keys or the runtime IAM role attached by DevOps.",
    inboundNote: "Configure SES receipt rules / SNS to POST to the webhook URL (advanced).",
    defaultProviderConfig: { region: "us-east-1", auth_mode: SES_AUTH_ACCESS_KEYS },
    fields: {
      clientSecret: {
        show: true,
        label: "AWS secret access key",
        placeholder: "••••••••",
        hint: "Leave blank to keep existing.",
      },
      clientToken: {
        show: true,
        label: "AWS access key ID",
        placeholder: "AKIA…",
        hint: "IAM user or role access key with ses:SendEmail.",
      },
      accessApiUrl: { show: false },
      tenantOrDomain: { show: false },
      apiScope: { show: false },
      providerConfigJson: { show: false },
      webhookSigningSecret: { show: false },
      webhookUrl: { show: true },
    },
  },
  brevo: {
    label: "Brevo",
    summary: "Brevo transactional API (formerly Sendinblue).",
    inboundNote: "Configure Brevo inbound webhooks to point at the URL below.",
    defaultProviderConfig: {},
    fields: {
      clientSecret: {
        show: true,
        label: "API key",
        placeholder: "xkeysib-…",
        hint: "Brevo v3 API key (api-key header). Leave blank to keep existing.",
      },
      clientToken: { show: false },
      accessApiUrl: {
        show: true,
        label: "API base URL (optional)",
        placeholder: "https://api.brevo.com/v3",
        hint: "Override only when needed.",
      },
      tenantOrDomain: { show: false },
      apiScope: { show: false },
      providerConfigJson: { show: false },
      webhookSigningSecret: { show: true, label: "Webhook secret (optional)" },
      webhookUrl: { show: true },
    },
  },
  mailgun: {
    label: "Mailgun",
    summary: "Mailgun HTTP API for a verified sending domain.",
    inboundNote: "Use Mailgun routes / webhooks with the URL below (HMAC signing recommended).",
    defaultProviderConfig: { mailgun_eu: false },
    fields: {
      clientSecret: {
        show: true,
        label: "Private API key",
        placeholder: "key-…",
        hint: "Mailgun private API key. Leave blank to keep existing.",
      },
      clientToken: { show: false },
      accessApiUrl: {
        show: true,
        label: "API base URL (optional)",
        placeholder: "https://api.mailgun.net/v3",
        hint: "Use https://api.eu.mailgun.net/v3 for EU (or enable EU in advanced config).",
      },
      tenantOrDomain: {
        show: true,
        label: "Sending domain",
        placeholder: "mg.yourdomain.com",
        hint: "Verified domain in Mailgun (required for send + domain checks).",
      },
      apiScope: { show: false },
      providerConfigJson: { show: true, label: "Advanced (JSON)" },
      webhookSigningSecret: {
        show: true,
        label: "Webhook signing key",
        placeholder: "From Mailgun webhook settings",
        hint: "Used to verify timestamp+token HMAC on inbound events.",
      },
      webhookUrl: { show: true },
    },
  },
  mandrill: {
    label: "Mandrill",
    summary: "Mailchimp Transactional (Mandrill) HTTP API.",
    inboundNote: "Inbound uses mandrill_events + X-Mandrill-Signature; webhook signing secret is strongly recommended.",
    defaultProviderConfig: {},
    fields: {
      clientSecret: {
        show: true,
        label: "API key",
        placeholder: "md-…",
        hint: "Mandrill API key. Leave blank to keep existing.",
      },
      clientToken: { show: false },
      accessApiUrl: {
        show: true,
        label: "API base URL (optional)",
        placeholder: "https://mandrillapp.com/api/1.0",
        hint: "Override only when needed.",
      },
      tenantOrDomain: { show: false },
      apiScope: { show: false },
      providerConfigJson: { show: true, label: "Advanced (JSON)" },
      webhookSigningSecret: {
        show: true,
        label: "Webhook signing secret",
        placeholder: "Required for inbound",
        hint: "Used to validate X-Mandrill-Signature on inbound POSTs.",
      },
      webhookUrl: { show: true },
    },
  },
  zendesk: {
    label: "Zendesk",
    summary:
      "Two-way ticket sync with Zendesk Support. Agents work in Zendesk; SentroDesk mirrors tickets and Zendesk notifies the customer. Set the Zendesk agent account email as the “From email” below — it is used for API auth.",
    inboundNote:
      "In Zendesk: create a Webhook pointing at the URL below and copy its Signing secret into the field. Then add two Triggers that fire the webhook — one on a public agent comment, one on a status change. See docs/ZENDESK_INTEGRATION.md for the exact JSON body.",
    defaultProviderConfig: {},
    fields: {
      clientSecret: {
        show: true,
        label: "API token",
        placeholder: "Zendesk API token",
        hint: "Admin Center → Apps and integrations → APIs → Zendesk API → Token Access. Leave blank to keep existing.",
      },
      clientToken: { show: false },
      accessApiUrl: {
        show: true,
        label: "Zendesk subdomain",
        placeholder: "acme",
        hint: "The “acme” in acme.zendesk.com (a full URL also works).",
      },
      tenantOrDomain: { show: false },
      apiScope: { show: false },
      providerConfigJson: { show: false },
      webhookSigningSecret: {
        show: true,
        label: "Webhook signing secret",
        placeholder: "From the Zendesk webhook",
        hint: "Required — verifies inbound Zendesk webhooks (HMAC-SHA256).",
      },
      webhookUrl: { show: true },
    },
  },
};

function parseProviderConfigJson(jsonStr) {
  try {
    const parsed = JSON.parse(jsonStr || "{}");
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) return parsed;
  } catch {
    /* ignore */
  }
  return {};
}

function stringifyProviderConfig(obj) {
  return JSON.stringify(obj ?? {}, null, 2);
}

function providerSchema(providerKey) {
  return PROVIDER_SCHEMA[providerKey] || PROVIDER_SCHEMA.smtp;
}

const MAX_TOTAL_SENDERS = 10;
const MAX_ADDITIONAL_SENDERS = MAX_TOTAL_SENDERS - 1;

function emptyAdditionalSender() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    email: "",
    name: "",
    useSharedConnection: true,
    clientToken: "",
    clientSecret: "",
    hasClientToken: false,
    hasClientSecret: false,
  };
}

function mapAdditionalSendersFromConfig(senders) {
  return (Array.isArray(senders) ? senders : [])
    .filter((row) => !row?.isDefault)
    .map((row) => ({
      id: String(row.email || emptyAdditionalSender().id),
      email: row.email || "",
      name: row.name || "",
      useSharedConnection: row.useSharedConnection !== false,
      clientToken: "",
      clientSecret: "",
      hasClientToken: Boolean(row.hasClientToken),
      hasClientSecret: Boolean(row.hasClientSecret),
    }));
}

function buildSenderIdentitiesPayload(rows) {
  return rows
    .filter((row) => String(row.email || "").trim())
    .map((row) => ({
      email: String(row.email).trim(),
      name: String(row.name || "").trim(),
      useSharedConnection: row.useSharedConnection !== false,
      ...(row.clientToken ? { clientToken: row.clientToken } : {}),
      ...(row.clientSecret ? { clientSecret: row.clientSecret } : {}),
    }));
}

const initialAutomations = {
  welcomeEmail: true,
  agentInvite: true,
  conversationDigest: false,
  slaAlerts: true,
  ticketCreated: false,
  passwordReset: true,
};

const automationTemplateMeta = [
  {
    key: "welcomeEmail",
    id: "auto-welcome",
    label: "Welcome / onboarding",
    description: "Sent when a new end-user profile is created.",
  },
  {
    key: "agentInvite",
    id: "auto-invite",
    label: "Agent invitations",
    description: "Invitation links for new workspace agents.",
  },
  {
    key: "conversationDigest",
    id: "auto-digest",
    label: "Conversation digests",
    description: "Periodic summaries for agents or managers.",
  },
  {
    key: "slaAlerts",
    id: "auto-sla",
    label: "SLA breach alerts",
    description: "Notify owners when thresholds are crossed.",
  },
  {
    key: "ticketCreated",
    id: "auto-ticket",
    label: "Ticket lifecycle",
    description: "Created, assigned, and resolved notifications.",
  },
  {
    key: "passwordReset",
    id: "auto-pw",
    label: "Security / account recovery",
    description: "Password reset and similar flows.",
  },
];

function buildInitialTemplates() {
  return automationTemplateMeta.reduce((acc, item) => {
    const defaults = themedDefaultTemplatesByKey[item.key] || { subject: "", body: "", html: "" };
    acc[item.key] = { subject: defaults.subject, body: defaults.body, html: defaults.html };
    return acc;
  }, {});
}

function normalizePrimaryColor(value) {
  const raw = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : DEFAULT_EMAIL_PRIMARY_COLOR;
}

function EmailTemplatePreview({ html, previewVariables, resolvedTheme }) {
  const srcDoc = useMemo(
    () => substituteEmailTemplateVars(html, previewVariables),
    [html, previewVariables],
  );
  if (!srcDoc.trim()) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 py-6 text-center text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-400">
        Add HTML to see a themed preview.
      </p>
    );
  }
  return (
    <div
      className={`overflow-hidden rounded-xl border ring-1 ${
        resolvedTheme === "dark"
          ? "border-slate-600/70 bg-slate-950 ring-slate-700/60"
          : "border-slate-200/80 bg-slate-100/90 ring-slate-200/80"
      }`}
    >
      <div
        className={`flex items-center justify-between gap-2 border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wide ${
          resolvedTheme === "dark"
            ? "border-slate-700/80 bg-slate-900/90 text-slate-400"
            : "border-slate-200/80 bg-white/90 text-slate-500"
        }`}
      >
        <span>Preview</span>
        <span className="font-mono normal-case text-slate-400">
          accent {previewVariables.primary_color}
        </span>
      </div>
      <iframe
        title="Email HTML preview"
        srcDoc={srcDoc}
        sandbox=""
        className="block h-[min(420px,55vh)] w-full border-0 bg-white"
      />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      {children}
      {hint ? <span className="block text-xs text-slate-400 dark:text-slate-500">{hint}</span> : null}
    </label>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="ui-card-static space-y-5 !p-6 sm:!p-8">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">{title}</h2>
        {description ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function ToggleRow({ id, label, description, on, onToggle }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3 ring-1 ring-slate-100 dark:border-slate-700/60 dark:bg-slate-800/45 dark:ring-slate-700/50">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</p>
        {description ? <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{description}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        id={id}
        onClick={onToggle}
        className={`inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
          on ? "ui-toggle-track-on" : "ui-toggle-track-off"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-slate-200/70 transition-transform duration-200 ease-out ${
            on ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

export default function EmailService() {
  const { activeWorkspaceId } = useWorkspace();
  const { user } = useAuthContext();
  const { resolvedTheme } = useTheme();
  const tenantId = user?.tenant_id ?? "";
  const [brandPrimaryColor, setBrandPrimaryColor] = useState(DEFAULT_EMAIL_PRIMARY_COLOR);
  const [clientSecret, setClientSecret] = useState("");
  const [clientToken, setClientToken] = useState("");
  const [accessApiUrl, setAccessApiUrl] = useState("");
  const [provider, setProvider] = useState("smtp");
  const [providerConfigJson, setProviderConfigJson] = useState("{}");
  const [hasClientSecret, setHasClientSecret] = useState(false);
  const [hasClientToken, setHasClientToken] = useState(false);
  const [hasWebhookSecret, setHasWebhookSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tenantOrDomain, setTenantOrDomain] = useState("");
  const [apiScope, setApiScope] = useState("");
  const [webhookSigningSecret, setWebhookSigningSecret] = useState("");
  const [notes, setNotes] = useState("");

  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [additionalSenders, setAdditionalSenders] = useState([]);

  const [automations, setAutomations] = useState(initialAutomations);
  const [emailTemplates, setEmailTemplates] = useState(buildInitialTemplates);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const toggleAuto = (key) => setAutomations((p) => ({ ...p, [key]: !p[key] }));
  const updateTemplate = (key, field, value) => {
    setEmailTemplates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const resetForm = () => {
    setClientSecret("");
    setClientToken("");
    setAccessApiUrl("");
    setProvider("smtp");
    setProviderConfigJson("{}");
    setHasClientSecret(false);
    setHasClientToken(false);
    setHasWebhookSecret(false);
    setTenantOrDomain("");
    setApiScope("");
    setWebhookSigningSecret("");
    setNotes("");
    setFromName("");
    setFromEmail("");
    setReplyTo("");
    setAdditionalSenders([]);
    setAutomations({ ...initialAutomations });
    setEmailTemplates(buildInitialTemplates());
    setError("");
    setSuccess("");
  };

  const previewVariables = useMemo(
    () =>
      buildEmailPreviewVariables({
        primaryColor: brandPrimaryColor,
        workspaceName: fromName.trim() || "Your workspace",
        fromName: fromName.trim() || "Support Team",
      }),
    [brandPrimaryColor, fromName],
  );

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let live = true;
    void (async () => {
      try {
        let settings = await getWidgetSettings();
        if (settings?.data == null || settings?.message === "not_initialized") {
          await postWidgetBootstrap({});
          settings = await getWidgetSettings();
        }
        const defaults = settings?.data?.widget_defaults;
        if (live && defaults && typeof defaults === "object") {
          const primary =
            defaults.primary_color ?? defaults.primaryColor ?? DEFAULT_EMAIL_PRIMARY_COLOR;
          setBrandPrimaryColor(normalizePrimaryColor(primary));
        }
      } catch {
        if (live) setBrandPrimaryColor(DEFAULT_EMAIL_PRIMARY_COLOR);
      }
    })();
    return () => {
      live = false;
    };
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let live = true;
    setLoading(true);
    setError("");
    void fetchTicketEmailConfig({ workspaceId: activeWorkspaceId })
      .then((cfg) => {
        if (!live || !cfg) return;
        setAccessApiUrl(cfg.accessApiUrl || "");
        setProvider(cfg.provider || "smtp");
        setProviderConfigJson(
          JSON.stringify(cfg.providerConfig && typeof cfg.providerConfig === "object" ? cfg.providerConfig : {}, null, 2),
        );
        setHasClientSecret(Boolean(cfg.hasClientSecret));
        setHasClientToken(Boolean(cfg.hasClientToken));
        setHasWebhookSecret(Boolean(cfg.hasWebhookSigningSecret));
        setTenantOrDomain(cfg.tenantOrDomain || "");
        setApiScope(cfg.apiScope || "");
        setNotes(cfg.notes || "");
        setFromName(cfg.fromName || "");
        setFromEmail(cfg.fromEmail || "");
        setReplyTo(cfg.replyTo || "");
        setAdditionalSenders(mapAdditionalSendersFromConfig(cfg.senders));
        if (cfg.automations && typeof cfg.automations === "object") {
          setAutomations((prev) => ({ ...prev, ...cfg.automations }));
        }
        if (cfg.templates && typeof cfg.templates === "object") {
          setEmailTemplates((prev) => {
            const next = { ...prev };
            for (const [key, tpl] of Object.entries(cfg.templates)) {
              if (tpl && (tpl.subject || tpl.body || tpl.html)) {
                next[key] = { ...prev[key], ...tpl };
              }
            }
            return next;
          });
        }
      })
      .catch((e) => {
        if (!live) return;
        setError(e instanceof Error ? e.message : "Failed to load email service config.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [activeWorkspaceId]);

  const handleSave = async () => {
    if (!activeWorkspaceId) {
      setError("Active workspace is required.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      let providerConfig;
      try {
        providerConfig = JSON.parse(providerConfigJson || "{}");
        if (typeof providerConfig !== "object" || providerConfig === null || Array.isArray(providerConfig)) {
          throw new Error("Provider config must be a JSON object.");
        }
      } catch (parseErr) {
        setError(parseErr instanceof Error ? parseErr.message : "Invalid provider config JSON.");
        setSaving(false);
        return;
      }
      await updateTicketEmailConnection({
        workspaceId: activeWorkspaceId,
        payload: {
          ...(clientSecret ? { clientSecret } : {}),
          ...(clientToken ? { clientToken } : {}),
          accessApiUrl,
          provider,
          tenantOrDomain,
          apiScope,
          ...(webhookSigningSecret ? { webhookSigningSecret } : {}),
          notes,
          fromName,
          fromEmail,
          replyTo,
          providerConfig,
          senderIdentities: buildSenderIdentitiesPayload(additionalSenders),
        },
      });
      await updateTicketEmailAutomations({
        workspaceId: activeWorkspaceId,
        automations,
      });
      await updateTicketEmailTemplates({
        workspaceId: activeWorkspaceId,
        templates: emailTemplates,
      });
      setHasClientSecret(hasClientSecret || Boolean(clientSecret));
      setHasClientToken(hasClientToken || Boolean(clientToken));
      setHasWebhookSecret(hasWebhookSecret || Boolean(webhookSigningSecret));
      setAdditionalSenders((rows) =>
        rows.map((row) => ({
          ...row,
          clientToken: "",
          clientSecret: "",
          hasClientToken: row.hasClientToken || Boolean(row.clientToken),
          hasClientSecret: row.hasClientSecret || Boolean(row.clientSecret),
        })),
      );
      setClientSecret("");
      setClientToken("");
      setWebhookSigningSecret("");
      setSuccess("Email service configuration saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save email service config.");
    } finally {
      setSaving(false);
    }
  };

  const handleProviderChange = (nextProvider) => {
    const key = PROVIDER_OPTIONS.some((o) => o.value === nextProvider) ? nextProvider : "smtp";
    setProvider(key);
    const schema = providerSchema(key);
    setProviderConfigJson(stringifyProviderConfig(schema.defaultProviderConfig));
    setError("");
    setSuccess("");
  };

  const handleTestConnection = async () => {
    if (!activeWorkspaceId) return;
    setTesting(true);
    setError("");
    setSuccess("");
    try {
      let providerConfig;
      try {
        providerConfig = JSON.parse(providerConfigJson || "{}");
        if (typeof providerConfig !== "object" || providerConfig === null || Array.isArray(providerConfig)) {
          throw new Error("Provider config must be a JSON object.");
        }
      } catch (parseErr) {
        setError(parseErr instanceof Error ? parseErr.message : "Invalid provider config JSON.");
        return;
      }
      const result = await testTicketEmailConnectivity({
        workspaceId: activeWorkspaceId,
        payload: {
          ...(clientSecret ? { clientSecret } : {}),
          ...(clientToken ? { clientToken } : {}),
          accessApiUrl,
          provider,
          tenantOrDomain,
          apiScope,
          ...(webhookSigningSecret ? { webhookSigningSecret } : {}),
          fromName,
          fromEmail,
          replyTo,
          providerConfig,
        },
      });
      if (result?.success) {
        setSuccess(result.message || "Connection test passed.");
      } else {
        setError(result?.message || "Connection test failed.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  const handleRefreshInbox = async () => {
    if (!activeWorkspaceId) return;
    setRefreshing(true);
    setError("");
    setSuccess("");
    try {
      const result = await pollTicketEmailInbox({ workspaceId: activeWorkspaceId });
      const fetched = Number(result?.fetched ?? 0);
      const processed = Number(result?.processed ?? 0);
      const deadLetterCount = Number(result?.dead_letter_count ?? 0);
      let message = `Inbox refreshed: ${fetched} fetched, ${processed} processed`;
      if (deadLetterCount > 0) {
        message += ` (${deadLetterCount} failed)`;
      }
      setSuccess(message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Inbox refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  const schema = providerSchema(provider);
  const fields = schema.fields;
  const providerConfigObj = parseProviderConfigJson(providerConfigJson);
  const showSesCredentialFields = sesShowsCredentialFields(provider, providerConfigObj);
  const webhookUrl =
    fields.webhookUrl?.show && provider && activeWorkspaceId && tenantId
      ? webhookUrlForProvider(provider, activeWorkspaceId, tenantId)
      : "";

  const patchProviderConfig = (patch) => {
    setProviderConfigJson(stringifyProviderConfig({ ...providerConfigObj, ...patch }));
  };

  return (
    <AppLayout>
      <div className="ui-page scrollbar-thin h-full overflow-y-auto">
        <header className="ui-shell sticky top-0 z-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 dark:text-teal-400">Delivery</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-3xl">
            Email service
          </h1>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            Configure how this workspace talks to your transactional email provider.
          </p>
        </header>

        <div className="mx-auto max-w-6xl space-y-8 px-6 py-8 sm:px-8">
          {loading && (
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/90 px-4 py-3 text-sm text-slate-700 ring-1 ring-slate-100 dark:border-slate-600/70 dark:bg-slate-800/50 dark:text-slate-200 dark:ring-slate-700/50">
              Loading email service configuration...
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-rose-200/80 bg-rose-50/90 px-4 py-3 text-sm text-rose-800 ring-1 ring-rose-100 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-200 dark:ring-rose-900/40">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/90 px-4 py-3 text-sm text-emerald-800 ring-1 ring-emerald-100 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/40">
              {success}
            </div>
          )}

          <SectionCard
            title="Email provider"
            description="Choose which adapter sends and receives mail for this workspace."
          >
            <Field label="Provider" hint="Switching resets advanced provider JSON to that provider’s defaults (save to persist).">
              <select
                value={provider}
                onChange={(e) => handleProviderChange(e.target.value)}
                className="ui-input-subtle w-full max-w-md text-sm"
                disabled={loading || saving}
              >
                {PROVIDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-sm text-slate-600 dark:text-slate-300">{schema.summary}</p>
            {schema.inboundNote ? (
              <p className="rounded-lg border border-slate-200/80 bg-slate-50/90 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-100 dark:border-slate-600/70 dark:bg-slate-800/50 dark:text-slate-300 dark:ring-slate-700/50">
                {schema.inboundNote}
              </p>
            ) : null}
          </SectionCard>

          <SectionCard
            title={`${schema.label} connection`}
            description="Credentials are encrypted at rest; leave secret fields blank to keep existing values."
          >
            <div className="flex flex-wrap gap-2 text-xs">
              {hasClientSecret && (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700 ring-1 ring-emerald-200">
                  API secret on file
                </span>
              )}
              {hasClientToken && (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700 ring-1 ring-emerald-200">
                  Client token on file
                </span>
              )}
              {hasWebhookSecret && (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700 ring-1 ring-emerald-200">
                  Webhook secret on file
                </span>
              )}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {fields.clientSecret?.show && showSesCredentialFields ? (
                <Field label={fields.clientSecret.label} hint={fields.clientSecret.hint}>
                  <input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    className="ui-input-subtle w-full font-mono text-sm"
                    placeholder={fields.clientSecret.placeholder}
                    autoComplete="off"
                  />
                </Field>
              ) : null}
              {fields.clientToken?.show && showSesCredentialFields ? (
                <Field label={fields.clientToken.label} hint={fields.clientToken.hint}>
                  <input
                    type="text"
                    value={clientToken}
                    onChange={(e) => setClientToken(e.target.value)}
                    className="ui-input-subtle w-full font-mono text-sm"
                    placeholder={fields.clientToken.placeholder}
                    autoComplete="off"
                  />
                </Field>
              ) : null}
              {fields.accessApiUrl?.show ? (
                <Field label={fields.accessApiUrl.label} hint={fields.accessApiUrl.hint}>
                  <input
                    type="text"
                    value={accessApiUrl}
                    onChange={(e) => setAccessApiUrl(e.target.value)}
                    className="ui-input-subtle w-full font-mono text-sm"
                    placeholder={fields.accessApiUrl.placeholder}
                    autoComplete="off"
                  />
                </Field>
              ) : null}
              {fields.tenantOrDomain?.show ? (
                <Field label={fields.tenantOrDomain.label} hint={fields.tenantOrDomain.hint}>
                  <input
                    type="text"
                    value={tenantOrDomain}
                    onChange={(e) => setTenantOrDomain(e.target.value)}
                    className="ui-input-subtle w-full font-mono text-sm"
                    placeholder={fields.tenantOrDomain.placeholder}
                    autoComplete="off"
                  />
                </Field>
              ) : null}
            </div>

            {provider === "ses" ? (
              <div className="space-y-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    checked={isSesIamRoleMode(providerConfigObj)}
                    onChange={(e) =>
                      patchProviderConfig({
                        auth_mode: e.target.checked ? SES_AUTH_IAM_ROLE : SES_AUTH_ACCESS_KEYS,
                      })
                    }
                  />
                  Use IAM role from the human-agent-service runtime (no access keys)
                </label>
                {isSesIamRoleMode(providerConfigObj) ? (
                  <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    DevOps must attach an IAM role with SES send permissions to the human-agent-service
                    task or instance. SentroDesk uses the ambient AWS credential chain; no keys are stored.
                  </p>
                ) : null}
                <Field label="AWS region" hint="Stored in provider config as region.">
                  <input
                    type="text"
                    value={String(providerConfigObj.region ?? "us-east-1")}
                    onChange={(e) => patchProviderConfig({ region: e.target.value.trim() || "us-east-1" })}
                    className="ui-input-subtle w-full max-w-xs font-mono text-sm"
                    placeholder="us-east-1"
                    autoComplete="off"
                  />
                </Field>
              </div>
            ) : null}

            {provider === "mailgun" ? (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  checked={Boolean(providerConfigObj.mailgun_eu)}
                  onChange={(e) => patchProviderConfig({ mailgun_eu: e.target.checked })}
                />
                Use Mailgun EU API (api.eu.mailgun.net)
              </label>
            ) : null}

            {fields.providerConfigJson?.show ? (
              <Field
                label={fields.providerConfigJson.label || "Advanced provider config (JSON)"}
                hint='Optional extras, e.g. {"subaccount":"..."} for Mandrill.'
              >
                <textarea
                  value={providerConfigJson}
                  onChange={(e) => setProviderConfigJson(e.target.value)}
                  className="ui-input-subtle min-h-[100px] w-full resize-y font-mono text-xs"
                  spellCheck={false}
                />
              </Field>
            ) : null}

            {fields.webhookSigningSecret?.show ? (
              <Field
                label={fields.webhookSigningSecret.label || "Webhook signing secret"}
                hint={fields.webhookSigningSecret.hint || "Leave blank to keep existing."}
              >
                <input
                  type="password"
                  value={webhookSigningSecret}
                  onChange={(e) => setWebhookSigningSecret(e.target.value)}
                  className="ui-input-subtle w-full font-mono text-sm"
                  placeholder={fields.webhookSigningSecret.placeholder || "Optional"}
                  autoComplete="off"
                />
              </Field>
            ) : null}
            {fields.webhookUrl?.show && webhookUrl ? (
              <Field
                label="Inbound webhook URL"
                hint="Paste into your provider dashboard. Includes workspace_id and tenant_id for routing without a login token."
              >
                <input type="text" readOnly value={webhookUrl} className="ui-input-subtle w-full font-mono text-xs" />
              </Field>
            ) : null}
            <Field label="Internal notes" hint="Non-secret context for your team.">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="ui-input-subtle min-h-[88px] w-full resize-y text-sm"
                placeholder="e.g. use EU region endpoint, limit 100k/month"
              />
            </Field>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {provider === "smtp" ? (
                <p className="mr-auto text-xs text-slate-500 dark:text-slate-400">
                  Uses saved SMTP/IMAP settings — Save first if you changed credentials.
                </p>
              ) : null}
              {provider === "smtp" ? (
                <button
                  type="button"
                  className="ui-btn-secondary"
                  onClick={handleRefreshInbox}
                  disabled={refreshing || loading || saving}
                >
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>
              ) : null}
              <button
                type="button"
                className="ui-btn-secondary"
                onClick={handleTestConnection}
                disabled={testing || loading || saving}
              >
                {testing ? "Testing…" : `Test ${schema.label} connection`}
              </button>
            </div>
          </SectionCard>

          <SectionCard
            title="Sender identity"
            description="What recipients see in their inbox. Must align with your provider’s verified senders."
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="From name" hint="Display name shown next to the address.">
                <input
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  className="ui-input-subtle w-full"
                  placeholder="Acme Support"
                />
              </Field>
              <Field label="From email" hint="Must be a domain or mailbox your provider has verified.">
                <input
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  className="ui-input-subtle w-full font-mono text-sm"
                  placeholder="hello@yourdomain.com"
                  autoComplete="off"
                />
              </Field>
            </div>
            <Field label="Reply-to (optional)" hint="If set, replies from customers go here instead of the from address.">
              <input
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                className="ui-input-subtle w-full font-mono text-sm"
                placeholder="support@yourdomain.com"
                autoComplete="off"
              />
            </Field>
            <div className="space-y-3 border-t border-slate-200/70 pt-4 dark:border-slate-600/60">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Additional sender addresses</p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Up to {MAX_ADDITIONAL_SENDERS} extra addresses ({1 + additionalSenders.length} of {MAX_TOTAL_SENDERS}{" "}
                    senders). Separate mailboxes need their own login; aliases can share the primary mailbox.
                  </p>
                </div>
                <button
                  type="button"
                  className="ui-btn-secondary !py-1.5 !text-xs"
                  disabled={additionalSenders.length >= MAX_ADDITIONAL_SENDERS || loading || saving}
                  onClick={() => setAdditionalSenders((rows) => [...rows, emptyAdditionalSender()])}
                >
                  Add sender
                </button>
              </div>
              {additionalSenders.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-200/80 bg-slate-50/60 px-3 py-2 text-xs text-slate-500 dark:border-slate-600/60 dark:bg-slate-900/40 dark:text-slate-400">
                  No additional senders yet. The primary address above is used for automations and as the default in
                  ticket replies.
                </p>
              ) : (
                <div className="space-y-3">
                  {additionalSenders.map((row, index) => (
                    <div
                      key={row.id}
                      className="rounded-xl border border-slate-200/80 bg-white/70 p-4 ring-1 ring-slate-100 dark:border-slate-600/70 dark:bg-slate-900/55 dark:ring-slate-700/50"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Sender {index + 2}</p>
                        <button
                          type="button"
                          className="text-xs font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400"
                          onClick={() => setAdditionalSenders((rows) => rows.filter((item) => item.id !== row.id))}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Display name">
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) =>
                              setAdditionalSenders((rows) =>
                                rows.map((item) => (item.id === row.id ? { ...item, name: e.target.value } : item)),
                              )
                            }
                            className="ui-input-subtle w-full"
                            placeholder="Acme Contact"
                          />
                        </Field>
                        <Field label="Email address">
                          <input
                            type="email"
                            value={row.email}
                            onChange={(e) =>
                              setAdditionalSenders((rows) =>
                                rows.map((item) => (item.id === row.id ? { ...item, email: e.target.value } : item)),
                              )
                            }
                            className="ui-input-subtle w-full font-mono text-sm"
                            placeholder="contact@yourdomain.com"
                            autoComplete="off"
                          />
                        </Field>
                      </div>
                      <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border-slate-300"
                          checked={row.useSharedConnection !== false}
                          onChange={(e) =>
                            setAdditionalSenders((rows) =>
                              rows.map((item) =>
                                item.id === row.id ? { ...item, useSharedConnection: e.target.checked } : item,
                              ),
                            )
                          }
                        />
                        <span>
                          Use same mailbox login as primary
                          <span className="mt-0.5 block text-xs font-normal text-slate-500 dark:text-slate-400">
                            Uncheck when this address has its own SMTP/IMAP username and password.
                          </span>
                        </span>
                      </label>
                      {row.useSharedConnection === false && provider === "smtp" ? (
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <Field
                            label="SMTP/IMAP username"
                            hint={
                              row.hasClientToken
                                ? "Leave blank to keep the username already stored on the server."
                                : undefined
                            }
                          >
                            <input
                              type="text"
                              value={row.clientToken}
                              onChange={(e) =>
                                setAdditionalSenders((rows) =>
                                  rows.map((item) =>
                                    item.id === row.id ? { ...item, clientToken: e.target.value } : item,
                                  ),
                                )
                              }
                              className="ui-input-subtle w-full font-mono text-sm"
                              placeholder="contact@yourdomain.com"
                              autoComplete="off"
                            />
                          </Field>
                          <Field
                            label="SMTP/IMAP password"
                            hint={
                              row.hasClientSecret
                                ? "Leave blank to keep the password already stored on the server."
                                : undefined
                            }
                          >
                            <input
                              type="password"
                              value={row.clientSecret}
                              onChange={(e) =>
                                setAdditionalSenders((rows) =>
                                  rows.map((item) =>
                                    item.id === row.id ? { ...item, clientSecret: e.target.value } : item,
                                  ),
                                )
                              }
                              className="ui-input-subtle w-full"
                              placeholder="••••••••"
                              autoComplete="new-password"
                            />
                          </Field>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            title="Automated emails"
            description="Toggle which system events should trigger outbound mail once the integration is live."
          >
            <div className="space-y-2">
              {automationTemplateMeta.map((item) => (
                <ToggleRow
                  key={item.key}
                  id={item.id}
                  label={item.label}
                  description={item.description}
                  on={automations[item.key]}
                  onToggle={() => toggleAuto(item.key)}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Email templates"
            description="Branded HTML layouts use {{primary_color}} from Widget generator and adapt to light/dark preview. Plain-text body is unchanged for simple clients."
          >
            <p className="rounded-lg border border-teal-200/70 bg-teal-50/60 px-3 py-2 text-xs text-teal-900 ring-1 ring-teal-200/60 dark:border-teal-800/50 dark:bg-teal-950/35 dark:text-teal-100 dark:ring-teal-800/40">
              Brand accent:{" "}
              <span className="inline-flex items-center gap-1.5 font-mono font-semibold">
                <span
                  className="inline-block h-3 w-3 rounded-full ring-1 ring-black/10 dark:ring-white/20"
                  style={{ backgroundColor: brandPrimaryColor }}
                  aria-hidden
                />
                {brandPrimaryColor}
              </span>
              {" "}
              — update in Widget generator. Placeholders include {"{{primary_color}}"}, {"{{workspace_name}}"},
              {"{{first_name}}"}, {"{{ticket_id}}"}.
            </p>
            <div className="space-y-4">
              {automationTemplateMeta.map((item) => (
                <div
                  key={`tpl-${item.key}`}
                  className="rounded-xl border border-slate-200/80 bg-white/70 p-4 ring-1 ring-slate-100 dark:border-slate-600/70 dark:bg-slate-900/55 dark:ring-slate-700/50"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{item.label}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                        automations[item.key]
                          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800/60"
                          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:ring-slate-600/60"
                      }`}
                    >
                      {automations[item.key] ? "enabled" : "disabled"}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <Field label="Subject">
                      <input
                        type="text"
                        value={emailTemplates[item.key]?.subject || ""}
                        onChange={(e) => updateTemplate(item.key, "subject", e.target.value)}
                        className="ui-input-subtle w-full"
                        placeholder="Email subject"
                      />
                    </Field>
                    <Field label="Body" hint="Supports placeholders like {{first_name}}, {{workspace_name}}, {{ticket_id}}.">
                      <textarea
                        value={emailTemplates[item.key]?.body || ""}
                        onChange={(e) => updateTemplate(item.key, "body", e.target.value)}
                        className="ui-input-subtle min-h-[120px] w-full resize-y text-sm"
                        placeholder="Email body"
                      />
                    </Field>
                    <Field
                      label="HTML template"
                      hint="Responsive layout with branded header. Uses {{primary_color}} at send time."
                    >
                      <textarea
                        value={emailTemplates[item.key]?.html || ""}
                        onChange={(e) => updateTemplate(item.key, "html", e.target.value)}
                        className="ui-input-subtle min-h-[160px] w-full resize-y font-mono text-xs"
                        placeholder="<!DOCTYPE html>..."
                      />
                    </Field>
                    <EmailTemplatePreview
                      html={emailTemplates[item.key]?.html || ""}
                      previewVariables={previewVariables}
                      resolvedTheme={resolvedTheme}
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="text-xs font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300"
                        onClick={() => {
                          const defaults = themedDefaultTemplatesByKey[item.key];
                          if (!defaults) return;
                          updateTemplate(item.key, "html", defaults.html);
                        }}
                      >
                        Reset HTML to branded default
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <div className="flex flex-wrap items-center justify-end gap-3 pb-10">
            <button type="button" className="ui-btn-secondary" onClick={resetForm}>
              Reset form
            </button>
            <button type="button" className="ui-btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? "Saving..." : "Save configuration"}
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
